import React, { useCallback, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { ArrowUpRightIcon, MapPinIcon } from "lucide-react";
import { useHackathon } from "../../contexts/HackathonContext";
import { initials } from "../../utils/progress";
import type { Mentor } from "../../types/hackathon";
import { SectionHeading } from "./SectionHeading";
import { MentorDialog } from "./MentorDialog";
import { mentorGroups, type GroupPanel } from "./mentorCategories";

// Text colours for groups sitting directly on the dark section background.
const defaultPanel: GroupPanel = {
  bg: "",
  border: "border-sun",
  heading: "text-cream",
  accent: "text-sun",
  body: "text-cream/85",
  muted: "text-cream/60"
};
export function MentorsSection() {
  const {
    state
  } = useHackathon();
  const [selected, setSelected] = useState<Mentor | null>(null);
  const close = useCallback(() => setSelected(null), []);
  const judges = state.mentors.filter(m => m.category === "judge");
  const uncategorised = state.mentors.filter(m => !m.category);
  return <section id="mentors" className="scroll-mt-20 bg-ink py-24 text-sun md:py-32" data-id="element-543" data-mp="yk4emjlifvub">
      <div className="mx-auto max-w-[1400px] px-5 md:px-8" data-id="element-544" data-mp="tm2wq1ztm2kf">
        <SectionHeading title="Mentors & judges" tone="light" intro="Founders, engineers, investors and the Circuit Launch team, guiding every team from first sketch to the Demo Day stage. Tap anyone for their bio and office hours." data-id="element-545" data-mp="pl0xhl5wlqig" />

        {judges.length > 0 && <div className="mt-16 md:mt-20" data-id="element-546" data-mp="1dcg25kl5kjwv">
            <div className="flex flex-wrap items-baseline justify-between gap-3 border-t-2 border-sun pt-5" data-id="element-547" data-mp="1ddr55i6rxnmx">
              <h3 className="font-display text-4xl uppercase text-cream md:text-5xl" data-id="element-548" data-mp="13llf4e031gad">Demo Day judges</h3>
              <p className="text-[12px] font-bold uppercase tracking-wide text-sun" data-id="element-549" data-mp="210zc04jup4r2">
                Tue Oct 13 · Pitches 2 PM PT · On site at Circuit Launch
              </p>
            </div>
            <ul className="mt-8 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3" data-id="element-550" data-mp="2f6hp597osnu7">
              {judges.map(m => <li key={m.id} data-id="element-551" data-mp="2k645vsmq4m7r">
                  <button type="button" onClick={() => setSelected(m)} className="group flex w-full flex-col text-left" data-id="element-552" data-mp="2k52rpfaevjlt">
                    <Portrait mentor={m} tile="bg-tomato text-ink" className="aspect-[4/5] text-[clamp(4rem,9vw,8rem)]" data-id="element-553" data-mp="2ae85gax60k0d" />
                    <span className="block pt-5" data-id="element-554" data-mp="1wi30utor7nx3">
                      <span className="block font-display text-[clamp(2rem,3.2vw,3rem)] uppercase leading-[0.95] text-cream" data-id="element-555" data-mp="21fcuymn38nr9">
                        {m.name}
                      </span>
                      <span className="mt-3 block text-[15px] text-cream/75" data-id="element-556" data-mp="1rgdpfcxmd88h">{m.title}</span>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wide text-sun" data-id="element-557" data-mp="3hstckfsdfmzy">
                        View profile
                        <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5" data-id="element-558" data-mp="3lslmowxey17f" />
                      </span>
                    </span>
                  </button>
                </li>)}
            </ul>
          </div>}

        {mentorGroups.map(g => {
        const people = state.mentors.filter(m => m.category && g.categories.includes(m.category));
        const list = g.id === "mentors" ? [...people, ...uncategorised] : people;
        if (list.length === 0) return null;
        const p = g.panel ?? defaultPanel;
        return <div key={g.id} className={`mt-20 md:mt-24 ${g.panel ? `${p.bg} -mx-5 rounded-[32px] px-5 pb-12 pt-8 md:-mx-0 md:px-10 md:pb-14 md:pt-10` : ""}`} data-id="element-559" data-mp="22uyvafgu4ha9">
              <div className={`grid gap-3 border-t-2 ${p.border} pt-5 md:grid-cols-[1fr_minmax(0,420px)] md:items-baseline`} data-id="element-560" data-mp="8j0m2diizled">
                <h3 className={`font-display text-4xl uppercase md:text-5xl ${p.heading}`} data-id="element-561" data-mp="3l8ujklsm1ap">
                  {g.title}
                </h3>
                <p className={`text-[15px] ${p.muted}`} data-id="element-562" data-mp="9yv4qdr5zcfy">{g.intro}</p>
              </div>
              <ul className="mt-8 grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-3 md:gap-x-6 lg:grid-cols-4" data-id="element-563" data-mp="eyr0jpppjxkf">
                {list.map(m => <li key={m.id} data-id="element-564" data-mp="jx2ae0g0682n">
                    <button type="button" onClick={() => setSelected(m)} className="group block w-full text-left" data-id="element-565" data-mp="3if7u7jt641yw">
                      <Portrait mentor={m} tile={g.tile} className="aspect-square text-[clamp(3rem,7vw,5.5rem)]" data-id="element-566" data-mp="3ne17bb4jlqyj" />
                      <span className={`mt-4 block font-display text-2xl uppercase leading-tight group-hover:underline md:text-3xl ${p.heading}`} data-id="element-567" data-mp="3sd4282qt6gss">
                        {m.name}
                      </span>
                      <span className={`mt-1 block text-[12px] font-semibold uppercase tracking-wide ${p.accent}`} data-id="element-568" data-mp="2znotjjmm9001">{m.expertise}</span>
                      <span className={`mt-1 block text-sm ${p.muted}`} data-id="element-569" data-mp="34lyllld1nkhf">{m.organization}</span>
                      {m.location && <span className={`mt-2 flex items-center gap-1.5 text-[13px] font-medium ${p.body}`} data-id="element-570" data-mp="2s5gwrzarxvut">
                          <MapPinIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-571" data-mp="2n5kz0hso70np" />
                          {m.location}
                        </span>}
                      {m.note && <span className={`mt-2 block text-[13px] font-medium ${g.panel ? p.heading : "text-tomato"}`} data-id="element-572" data-mp="2e5y0q4v8mayv">{m.note}</span>}
                    </button>
                  </li>)}
              </ul>
            </div>;
      })}
      </div>

      <AnimatePresence data-id="element-573" data-mp="24da357wfo2z3">{selected && <MentorDialog mentor={selected} onClose={close} data-id="element-574" data-mp="1zh1tga1091zb" />}</AnimatePresence>
    </section>;
}
function Portrait({
  mentor,
  tile,
  className
}: {
  mentor: Mentor;
  tile: string;
  className: string;
}) {
  return <span className={`block overflow-hidden rounded-[28px] ${tile} ${className}`} data-id="element-575" data-mp="1qflh3wqmqpsg">
      {mentor.photo ? <img src={mentor.photo} alt={mentor.name} style={mentor.photoPosition ? {
      objectPosition: mentor.photoPosition
    } : undefined} className="h-full w-full object-cover transition-transform duration-200 ease-out group-hover:scale-[1.03]" data-id="element-576" data-mp="1ftot6usq2tli" /> : <span className="flex h-full items-center justify-center font-display leading-none transition-transform duration-200 ease-out group-hover:scale-[1.04]" aria-hidden="true" data-id="element-577" data-mp="1bu6dcdfrd8pj">
          {initials(mentor.name)}
        </span>}
    </span>;
}